import React from 'react';
import {ArrowLeftOutlined} from "@ant-design/icons";
import {AddButtonWithIcon} from "../../../../components/AntCostomComponents/Buttons";
import {useNavigate, useParams} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {Button, Card, Modal} from "antd";
import LoadingSpinner from "../../../../components/AntCostomComponents/LoadingSpinner";
import {getAllAnalysis, getPatientFilesList} from "../../../../utils/api-lists";
import AnalysisResultsComponent from "./analysis_results.component";
import FaceIdIcon from "../../../../assets/wrinkle_analysis.png";
import PlaceholderView from "../../components/PlaceholderView";

const {Meta} = Card;

const AiAnalysisPage = () => {
    const navigate = useNavigate();
    const [analysisFiles, setAnalysisFiles] = React.useState([]);
    const [patientFiles, setPatientFiles] = React.useState([]);
    const {t} = useTranslation("common");
    const {patientId} = useParams();
    const [loading, setLoading] = React.useState(true);
    const [selectedAnalysis, setSelectedAnalysis] = React.useState(null);
    const [groupedAnalyses, setGroupedAnalyses] = React.useState([]);
    const [error, setError] = React.useState(null);


    const groupAnalysisByImageId = (analysisFiles, patientFiles) => {
        // Skip analyses with empty files
        console.log('analysisFiles', analysisFiles);
        console.log('patientFiles', patientFiles);
        const validAnalyses = analysisFiles.filter(analysis =>
            analysis.files && Object.keys(analysis.files).length > 0
        );

        // Create a map of patient files for quick lookup
        const patientFilesMap = patientFiles.reduce((acc, file) => {
            acc[file.id] = file;
            return acc;
        }, {});

        // Group analyses by image ID
        const groupedAnalyses = validAnalyses.reduce((groups, analysis) => {
            const imageId = analysis.image_id;

            // Check if image exists in patient files or create a standalone group
            const targetGroupId = patientFilesMap[imageId] ? imageId : `standalone-${analysis.id}`;

            if (!groups[targetGroupId]) {
                // Initialize new group
                const patientFile = patientFilesMap[imageId];
                groups[targetGroupId] = {
                    image_id: imageId,
                    analysis_count: 0,
                    image_url: patientFile?.url || analysis.files.original_image?.jpg || analysis.files.original_image?.png,
                    image_name: patientFile?.image_name || "Analysis Image",
                    date: new Date(analysis.created_at),
                    analyses: []
                };
            } else {
                // Update date if this analysis is more recent
                const analysisDate = new Date(analysis.created_at);
                if (analysisDate > groups[targetGroupId].date) {
                    groups[targetGroupId].date = analysisDate;
                }
            }

            // Add analysis to the group
            groups[targetGroupId].analysis_count++;

            // Process analysis files
            const processedAnalysis = {
                id: analysis.id,
                analysis_type: analysis.analysis_type,
                created_at: analysis.created_at
            };

            // Extract file URLs based on analysis type
            if (analysis.analysis_type === 1) { // Wrinkle detection
                processedAnalysis.data_url = analysis.files['grading_results.json'];
                processedAnalysis.original_image_url = analysis.files['original_image.png'];
                processedAnalysis.visualization_url = analysis.files['visualization.png'];
                processedAnalysis.wrinkle_mask_url = analysis.files['wrinkle_mask.png'];
            } else if (analysis.analysis_type === 2) { // Acne detection
                processedAnalysis.data_url = analysis.files['detections.json'];
                processedAnalysis.original_image_url = analysis.files['original_image.jpg'];
                processedAnalysis.visualization_url = analysis.files['visualization.jpg'];
            }

            groups[targetGroupId].analyses.push(processedAnalysis);

            return groups;
        }, {});

        // Convert to array and sort by date (newest first)
        return Object.values(groupedAnalyses)
            .sort((a, b) => b.date - a.date);
    };

    const fetchAnalysisFiles = async () => {
        setLoading(true);
        setError(null); // Reset error state

        try {
            const [filesResponse, analysisResponse] = await Promise.all([
                getPatientFilesList(patientId),
                getAllAnalysis(patientId)
            ]);

            if (analysisResponse.data && analysisResponse.data.error) {
                // Handle specific error message from API
                setError(analysisResponse.data.error);
                setGroupedAnalyses([]);
            } else if (filesResponse.data && analysisResponse.data) {
                setAnalysisFiles(analysisResponse.data);
                setPatientFiles(filesResponse.data);

                // Group analyses by image ID
                const grouped = groupAnalysisByImageId(analysisResponse.data, filesResponse.data);
                setGroupedAnalyses(grouped);
                console.log('Grouped Analyses:', grouped);
            }
        } catch (err) {
            console.error("Error fetching analysis data:", err);
            setError("Failed to load analysis data");
            setGroupedAnalyses([]);
        }

        setLoading(false);
    }

    const cardClickHandler = (analysis) => {
        setSelectedAnalysis(analysis);
    }

    const handleOk = () => {
        setSelectedAnalysis(null);
    }

    React.useEffect(() => {
        fetchAnalysisFiles();
    }, [patientId]);

    return (
        <div>
            <div className="flex flex-row justify-between gap-4 mb-4 m-6">
                <Button
                    icon={<ArrowLeftOutlined/>}
                    onClick={() => navigate(-1)}
                    type="text"
                >
                </Button>
                <h3 className="w-full text-left">{t("ai_analysis")}</h3>
                {analysisFiles.length > 0 && (
                    <AddButtonWithIcon
                        isTextHiddenOnMobile={true}
                        type="primary"
                        text={"Select File"}
                        onClick={() => {
                            navigate("../analysis");
                        }}
                    />
                )}
            </div>
            {
                loading && (
                    <LoadingSpinner/>
                )
            }

            <div className="flex flex-row flex-wrap gap-4 justify-center items-center">
                {groupedAnalyses.length > 0 && groupedAnalyses.map((group) => (
                    <Card
                        hoverable
                        onClick={() => cardClickHandler(group)}
                        style={{width: 300}}
                        cover={
                            <div style={{overflow: "hidden", height: "300px"}}>
                                <img alt="card_image" src={group.image_url}
                                     style={{height: '100%', objectFit: 'cover', width: '100%'}}/>
                            </div>
                        }
                    >
                        <Meta title={group.image_name} description={new Date(group.date).toLocaleDateString()}/>
                    </Card>
                ))}

                <Modal
                    title="Analysis"
                    closable={{'aria-label': 'Custom Close Button'}}
                    open={selectedAnalysis !== null}
                    onCancel={handleOk}
                    width={'100%'}
                    style={{maxWidth: '1200px'}}
                    footer={null}
                    destroyOnClose={true}
                >
                    <AnalysisResultsComponent analysis={selectedAnalysis}/>
                </Modal>

                {groupedAnalyses.length === 0 && !loading && (
                    <div className="col-span-full text-center py-8 text-gray-500">
                        <PlaceholderView
                            image={FaceIdIcon}
                            title={t("no_wrinkle_analysis")}
                            description={t("wrinkle_description")}
                            buttonText={t("analyze_now")}
                            onButtonClick={() => navigate("../analysis")}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default AiAnalysisPage;